import { useTranslate } from '@va/localization';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DropzoneOptions, FileRejection, useDropzone } from 'react-dropzone';

export const useFileUpload = ({
  onChange,
  value,
  disabled,
  onError,
  acceptedMimeTypes = ['image/svg+xml', 'image/png', 'image/jpeg'],
}: {
  onChange: (file: File) => void;
  onError: (message: string) => void;
  value: string | File | null;
  disabled?: boolean;
  acceptedMimeTypes?: string | string[];
}) => {
  const [preview, setPreview] = useState<any>();
  const translate = useTranslate();

  const fileErrorText = useCallback(
    (errorCode: string) => {
      if (errorCode === 'file-too-large') {
        return translate('file.error.tooLarge');
      }
      if (errorCode === 'file-invalid-type') {
        return translate('file.error.notSupported');
      }
      return translate('file.error.somethingWentWrong');
    },
    [translate],
  );

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (fileRejections.length > 0) {
        const [{ errors }] = fileRejections;

        onError(fileErrorText(errors[0]?.code));

        return;
      }

      onChange(acceptedFiles[0]);

      readFileAsDataURL(acceptedFiles[0]).then(setPreview);
    },
    [fileErrorText, onChange, onError],
  );

  const dropzoneSettings = useMemo<DropzoneOptions>(
    () => ({
      onDrop,
      // react-dropzone expects { mimeType: extensions[] }; an empty list accepts any extension of that MIME type
      accept: Object.fromEntries([acceptedMimeTypes].flat().map((mimeType) => [mimeType, []])),
      maxFiles: 1,
      maxSize: 1048576,
      disabled: disabled,
    }),
    [onDrop, acceptedMimeTypes, disabled],
  );
  const { getRootProps, getInputProps, isDragActive } = useDropzone(dropzoneSettings);

  useEffect(() => {
    if (value instanceof File) {
      readFileAsDataURL(value).then(setPreview);
      return;
    }

    setPreview(value);
  }, [value]);

  return { getRootProps, getInputProps, preview, isDragActive, setPreview };
};

function readFileAsDataURL(file: File): Promise<string | ArrayBuffer | null | undefined> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = function (e) {
      resolve(e.target?.result);
    };

    reader.onerror = function () {
      reject(new Error('Failed to read file'));
    };

    reader.readAsDataURL(file);
  });
}
